import React, { useEffect, useState, useCallback, useReducer } from 'react';

import { UserProfile } from '@repo/domain/user';
import { withEventScope } from '@repo/event-processor';
import { useDebounce } from '@repo/react-hooks/useDebounce';
import { buildTrie } from '@repo/react-hooks/useTrie';
import CloseIcon from '@repo/ui/components/Icon/CloseIcon';
import SearchIcon from '@repo/ui/components/Icon/SearchIcon';
import { TextInput } from '@repo/ui/components/Inputs';
import { Spacer } from '@repo/ui/components/Spacer';
import { H5, H6, P4 } from '@repo/ui/components/Typography';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { themeStyle } from '@repo/ui/theming';
import { buildProfileImageUrl } from '@repo/utils/url';

import { SearchProps } from 'components/Search/types';
import userApi from 'global/userApi';
import { FlexColumn, FlexRow } from 'styles/layouts';

import { Section, Suggestion, SuggestionsWrapper, Wrapper } from './styles';

const SUGGESTION_LIMIT = 5;
const USERS_SUGGESTION_LIMIT = 3;
const ARROW_UP = 'ArrowUp';
const ARROW_DOWN = 'ArrowDown';
const KEY_ENTER = 'Enter';

// white space tokenize a given query string
const tokenize = (query: string): string[] =>
  query.split(' ').filter((token) => token !== '');

const trieReducer = (
  trie: ReturnType<typeof buildTrie> | null,
  action: { type: 'INIT' | 'UPDATE'; payload: string[] }
): ReturnType<typeof buildTrie> | null => {
  switch (action.type) {
    case 'INIT':
      // If the trie is built already - nothing to do here, otherwise - build the trie
      return trie ?? buildTrie(action.payload);
    case 'UPDATE':
      // If the trie is not defined - nothing to update, skip the stage, otherwise - update existing trie
      return trie ? buildTrie(action.payload) : trie;
    default:
      return trie;
  }
};

const Search: React.FC<SearchProps> = ({
  enableUserSearch = true,
  ...props
}) => {
  const [query, setQuery] = useState<string>(props.query);

  useEffect(() => {
    setQuery(props.query);
  }, [props.query]);

  const [trie, dispatchTrie] = useReducer(trieReducer, null);
  const [matches, setMatches] = useState<{
    prefixes: string[];
    tags: string[];
    queryUsedToMatch: string;
  }>();
  const [suggestedUsers, setSuggestedUsers] = useState<UserProfile[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState<number | null>(null);
  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);

  useEffect(() => {
    dispatchTrie({ type: 'UPDATE', payload: props.tags });
  }, [props.tags]);

  const buildTagsTrie = (): void => {
    dispatchTrie({ type: 'INIT', payload: props.tags });
  };

  const reset = useCallback(() => {
    setDisplayDropdown(false);
    setSuggestedUsers([]);
    setQuery('');
    props.resetQuery();
  }, [props]);

  useEffect(() => {
    if (!props.parentState) {
      return;
    }
    if (!props.parentState?.isOpen) {
      setDisplayDropdown(!!query);
    }
  }, [props.parentState, reset, query]);

  const onSelectOption = (option: string): void => {
    setDisplayDropdown(false);
    setSuggestedUsers([]);
    setQuery(option);
    props.onSelectOption?.(option);
  };

  const onCommit = (query: string): void => {
    setDisplayDropdown(false);
    setSuggestedUsers([]);
    props.onCommit(query);
  };

  const updateUsers = useCallback(async (query: string): Promise<void> => {
    if (query.length) {
      const suggestedUsers = await userApi.findUser(query, true);
      if ('results' in suggestedUsers) {
        setSuggestedUsers(
          suggestedUsers.results.slice(0, USERS_SUGGESTION_LIMIT)
        );
        return;
      }
    }
    setSuggestedUsers([]);
  }, []);

  const debounceUpdateUsers = useDebounce({
    callback: updateUsers,
    delay: 500,
    waitForPromises: true,
  });

  const handleBlur = (): void => {
    // We clear the query if there is no input value. Otherwise it is not
    // obvious that the query has not been cleared.
    if (!query) {
      reset();
    }
  };

  const handleChange = (query: string): void => {
    setDisplayDropdown(!!query?.length);
    setActiveSuggestion(null);
    setQuery(query);

    // Populate users data in search result conditionally
    enableUserSearch && debounceUpdateUsers(query);

    query = query.toLowerCase();

    let matches: { tag: string }[] = [];
    const prefixes: string[] = [];
    const trimmedQuery = query.trim();
    const tokenizedQuery = tokenize(trimmedQuery);

    while (!matches.length && tokenizedQuery.length) {
      matches = trie
        ? trie.getMatches(tokenizedQuery.join(' '), {
            limit: SUGGESTION_LIMIT,
          })
        : [];

      matches = matches.filter((match) => {
        return match.tag.includes(tokenizedQuery.join(' '));
      });

      if (!matches.length) {
        const rest = tokenizedQuery.shift();
        if (rest) {
          prefixes.push(rest);
        }
      }
    }

    // Add the current query as a default match if it's not already in the matches
    if (trimmedQuery && !matches.find((match) => match.tag === trimmedQuery)) {
      matches.push({ tag: trimmedQuery });
    }

    setMatches({
      prefixes,
      tags: matches.map((match) => match.tag),
      queryUsedToMatch: tokenizedQuery.join(' '),
    });
  };

  const isDefaultSuggestion = (tag: string): boolean => {
    return tag === query.trim();
  };

  const getFullSuggestion = (index: number): string => {
    const tag = matches?.tags[index] || '';

    if (isDefaultSuggestion(tag)) {
      return tag;
    }
    let prefix = matches?.prefixes.join(' ') ?? '';
    if (prefix.length) {
      prefix += ' ';
    }
    return prefix + tag;
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    const totalSuggestionsLength =
      (matches ? matches.tags.length : 0) + suggestedUsers.length;

    if (event.code === KEY_ENTER) {
      if (activeSuggestion === null) {
        onCommit(query);
        return;
      }

      if (totalSuggestionsLength) {
        const matchesLength = matches ? matches.tags.length : 0;
        if (activeSuggestion < matchesLength) {
          const fullSuggestion = getFullSuggestion(activeSuggestion);
          setQuery(fullSuggestion);
          onSelectOption(fullSuggestion);
        } else {
          props.onSelectUser?.(
            suggestedUsers[activeSuggestion - matchesLength]
          );
        }
        return;
      }
    }

    if ([ARROW_DOWN, ARROW_UP].includes(event.key)) {
      event.preventDefault();

      const directions: Record<string, number> = {
        [ARROW_DOWN]: 1,
        [ARROW_UP]: -1,
      };

      setActiveSuggestion((active: number | null): number | null => {
        if (totalSuggestionsLength) {
          if (active === null) {
            active = -1;
          }

          let next = (active + directions[event.key]) % totalSuggestionsLength;
          if (next < 0) {
            next = totalSuggestionsLength - 1;
          }

          return next;
        }
        return null;
      });
    }
  };

  const debounceHandleKeyDown = useDebounce({
    callback: handleKeyDown,
    delay: 250,
  });

  return (
    <Wrapper>
      <TextInput
        autoComplete="off"
        blurOnEnter={false}
        dataTestId={props.dataTestId}
        icon={
          props.showLeftIcon
            ? { component: SearchIcon, position: 'start' }
            : undefined
        }
        inputRef={props.inputRef}
        name="search"
        placeholder={props.placeholder || 'Search'}
        rightIcon={
          query.length
            ? { component: CloseIcon, label: 'Reset search', onClick: reset }
            : props.showRightIcon
              ? { component: SearchIcon, label: 'Search', onClick: reset }
              : undefined
        }
        theme={{
          rightIcon: {
            height: '12px',
            color: themeStyle.varInkMedium,
            activeColor: themeStyle.varInkMain,
          },
          ...props.inputTheme,
          icon: {
            height: '12px',
            color: 'var(--color-icon-secondary)',
            colorActive: 'var(--color-icon-secondary)',
          },
        }}
        value={query}
        onBlur={handleBlur}
        onChanging={handleChange}
        onFocus={buildTagsTrie}
        onKeyDown={debounceHandleKeyDown}
      />
      {!props.disableSuggestions && displayDropdown && (
        <SuggestionsWrapper
          allowHorizontalDropdownGrowth={!!props.allowHorizontalDropdownGrowth}
          fullPageSuggestions={!!props.fullPageSuggestions}
        >
          {!!(matches?.tags.length || suggestedUsers.length) && (
            <FlexColumn>
              {!!matches?.tags.length && (
                <Section>
                  {matches.tags.map((tag, index) => {
                    const displayPrefix =
                      matches.prefixes.length && !isDefaultSuggestion(tag)
                        ? `
                     ${matches.prefixes.length > 1 ? '...' : ''}
                     ${matches.prefixes[matches.prefixes.length - 1]}`
                        : '';

                    const highlighted = tag.slice(
                      tag.indexOf(matches.queryUsedToMatch) +
                        matches.queryUsedToMatch.length
                    );
                    const nonHighlighted = tag.slice(
                      0,
                      tag.indexOf(matches.queryUsedToMatch) +
                        matches.queryUsedToMatch.length
                    );

                    return (
                      <Suggestion
                        key={`suggestion-${index}`}
                        height="33px"
                        isActive={index === activeSuggestion}
                        style={{ flexWrap: 'nowrap' }}
                        onClick={(): void =>
                          onSelectOption(getFullSuggestion(index))
                        }
                        onPointerEnter={(): void => {
                          setActiveSuggestion(index);
                        }}
                      >
                        <SearchIcon
                          height="12px"
                          theme={{ color: themeStyle.varInkMedium }}
                        />
                        <Spacer width="10px" />
                        <H5 noTextOverflow style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ color: themeStyle.varInkMedium }}>
                            {displayPrefix +
                              (displayPrefix.length ? ' ' : '') +
                              nonHighlighted}
                          </span>
                          <span style={{ color: themeStyle.varInkMain }}>
                            {highlighted}
                          </span>
                        </H5>
                      </Suggestion>
                    );
                  })}
                </Section>
              )}
              {!!suggestedUsers.length && (
                <Section>
                  <H5 style={{ marginBottom: '8px', textAlign: 'left' }}>
                    Members
                  </H5>
                  {suggestedUsers.map((user, index) => (
                    <Suggestion
                      key={user.slug}
                      height="46px"
                      isActive={
                        activeSuggestion === index + (matches?.tags.length || 0)
                      }
                      onClick={(): void => props.onSelectUser?.(user)}
                      onPointerEnter={(): void => {
                        setActiveSuggestion(
                          index + (matches?.tags.length || 0)
                        );
                      }}
                    >
                      <FlexRow
                        alignItems="center"
                        gap="6px"
                        height="100%"
                        style={{ overflow: 'hidden' }}
                      >
                        <ProfileImage
                          profile={{
                            ...user,
                            profileImage: buildProfileImageUrl(
                              user.profileImage
                            ),
                          }}
                          size="30px"
                        />
                        <FlexColumn style={{ overflow: 'hidden' }}>
                          <H6 noTextOverflow>{user.name}</H6>
                          <P4 noTextOverflow color={themeStyle.varInkMedium}>
                            @{user.slug}
                          </P4>
                        </FlexColumn>
                      </FlexRow>
                    </Suggestion>
                  ))}
                </Section>
              )}
            </FlexColumn>
          )}
        </SuggestionsWrapper>
      )}
    </Wrapper>
  );
};

export default withEventScope(Search, { module: 'search' });
